import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useSEO } from "@/lib/seo";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowRight, Handshake, Circle, CheckCircle2 } from "lucide-react";

const STATUS_LABEL = {
  opened: "Opened",
  due_diligence: "Due Diligence",
  funds_pending: "Funds Pending",
  funds_cleared: "Funds Cleared",
  buyer_accepted: "Buyer Accepted",
  funds_released: "Funds Released",
  closed: "Closed",
};

export default function Deals() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({ title: "My Deals — FinanceLocal Transaction Rooms", noIndex: true, canonical: "https://myfinancelocal.com/deals" });

  useEffect(() => {
    api.get("/deals/mine").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);

  if (!user) return null;

  return (
    <div data-testid="page-deals" className="mx-auto max-w-6xl px-4 py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="font-overline text-accent flex items-center gap-2"><Handshake className="w-4 h-4" /> My Deals</div>
        <h1 className="font-display text-4xl mt-2">Transaction Rooms</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Every accepted offer opens a Deal Room where the buyer and seller exchange documents,
          confirm status, and record the transaction. Deal Rooms are private to the two parties
          and platform admins.
        </p>
      </div>
      {loading && <div className="text-muted-foreground" data-testid="deals-loading">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="border border-border p-12 text-center text-muted-foreground" data-testid="deals-empty">
          You don&apos;t have any active deals yet. Accepted offers on your listings — or on notes you offered on — will appear here.
        </div>
      )}
      <div className="grid gap-4" data-testid="deals-grid">
        {items.map((d) => (
          <Link key={d.deal_id} to={`/deals/${d.deal_id}`} data-testid={`deal-row-${d.deal_id}`}
                className="border border-border p-4 flex items-center justify-between hover:border-primary transition-colors">
            <div>
              <div className="font-mono-data text-xs text-muted-foreground">{d.deal_id}</div>
              <div className="font-display text-xl mt-1">{d.note?.vehicle_year} {d.note?.vehicle_make} {d.note?.vehicle_model}</div>
              <div className="text-xs text-muted-foreground mt-1">You are the <b className="text-foreground">{d.your_role}</b></div>
            </div>
            <div className="text-right space-y-1">
              <StatusPill status={d.status} />
              <div className="font-mono-data text-primary">{fmtMoney(d.final_amount || 0)}</div>
              <ArrowRight className="w-4 h-4 text-muted-foreground inline-block" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function StatusPill({ status }) {
  const opened = status === "opened";
  return (
    <span data-testid={`deal-status-${status}`}
      className={"inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider border " +
        (opened ? "border-amber-400 text-amber-700 dark:text-amber-300" : "border-primary text-primary")}>
      {opened ? <Circle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
      {STATUS_LABEL[status] || status}
    </span>
  );
}
